import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { type ScheduleSettings, type HolidayInfo } from '@/lib/scheduleUtils';

const IS_DEV = import.meta.env.DEV;

function perfLog(label: string, ms: number) {
  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.log(`[SchedulePerf] ${label}: ${ms.toFixed(1)}ms`);
  }
}

const DEFAULT_SETTINGS: ScheduleSettings = {
  id: '',
  project_id: '',
  working_days: [1, 2, 3, 4, 5, 6],
  sunday_off: true,
  saturday_off: true,
  exclude_public_holidays: true,
  holiday_country: 'NZ',
  holiday_region: null,
  created_at: '',
  updated_at: '',
};

// Module-level cache so ScheduleTab doesn't re-fetch on every tab switch
const SETTINGS_CACHE_TTL_MS = 60_000; // 1 minute
export const settingsCache = new Map<string, {
  settings: ScheduleSettings;
  holidays: HolidayInfo[];
  projectHolidays: HolidayInfo[];
  timestamp: number;
}>();

export interface UseScheduleSettingsResult {
  settings: ScheduleSettings;
  holidays: HolidayInfo[];
  projectHolidays: HolidayInfo[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  reloadHolidays: () => Promise<void>;
}

export function useScheduleSettings(projectId: string | undefined): UseScheduleSettingsResult {
  const cached = projectId ? settingsCache.get(projectId) : undefined;
  const [settings, setSettings] = useState<ScheduleSettings>(cached?.settings || DEFAULT_SETTINGS);
  const [holidays, setHolidays] = useState<HolidayInfo[]>(cached?.holidays || []);
  const [projectHolidays, setProjectHolidays] = useState<HolidayInfo[]>(cached?.projectHolidays || []);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);

  const isLoadingRef = useRef(false);
  const lastProjectIdRef = useRef<string | undefined>(undefined);

  const loadSettings = useCallback(async (forceRefresh = false) => {
    if (!projectId) return;
    if (isLoadingRef.current) return;

    // Check module-level cache first
    if (!forceRefresh) {
      const c = settingsCache.get(projectId);
      if (c && Date.now() - c.timestamp < SETTINGS_CACHE_TTL_MS) {
        setSettings(c.settings);
        setHolidays(c.holidays);
        setProjectHolidays(c.projectHolidays);
        setLoading(false);
        lastProjectIdRef.current = projectId;
        return;
      }
    }

    if (lastProjectIdRef.current === projectId && settings.id && !forceRefresh) return;

    isLoadingRef.current = true;
    setLoading(true);
    const t0 = performance.now();

    try {
      const { data } = await supabase
        .from('jphph_project_schedule_settings')
        .select('*')
        .eq('project_id', projectId)
        .maybeSingle();

      let finalSettings: ScheduleSettings;
      if (data) {
        finalSettings = data as ScheduleSettings;
        setSettings(finalSettings);
      } else {
        const defaultSettings = {
          project_id: projectId,
          working_days: [1, 2, 3, 4, 5, 6],
          sunday_off: true,
          saturday_off: true,
          exclude_public_holidays: true,
          holiday_country: 'NZ',
          holiday_region: null,
        };
        const { data: inserted } = await supabase
          .from('jphph_project_schedule_settings')
          .insert(defaultSettings)
          .select()
          .single();
        finalSettings = inserted ? (inserted as ScheduleSettings) : DEFAULT_SETTINGS;
        if (inserted) setSettings(finalSettings);
      }

      // Load holidays in parallel
      const country = finalSettings.holiday_country || 'NZ';
      const [{ data: pubData }, { data: projData }] = await Promise.all([
        supabase
          .from('jphph_schedule_holidays')
          .select('holiday_date, name, holiday_name')
          .eq('country_code', country)
          .eq('is_active', true),
        supabase
          .from('jphph_project_schedule_holidays')
          .select('holiday_date, name, holiday_name')
          .eq('project_id', projectId)
          .eq('is_active', true),
      ]);

      const newHolidays = (pubData || []).map((h) => {
        const title = (h as Record<string, unknown>).holiday_name || (h as Record<string, unknown>).name || 'Holiday';
        return { date: h.holiday_date as string, name: String(title) };
      });
      const newProjectHolidays = (projData || []).map((h) => {
        const title = (h as Record<string, unknown>).holiday_name || (h as Record<string, unknown>).name || 'Holiday';
        return { date: h.holiday_date as string, name: String(title) };
      });

      setHolidays(newHolidays);
      setProjectHolidays(newProjectHolidays);

      // Populate cache
      settingsCache.set(projectId, {
        settings: finalSettings,
        holidays: newHolidays,
        projectHolidays: newProjectHolidays,
        timestamp: Date.now(),
      });

      perfLog('loadSettings', performance.now() - t0);
      lastProjectIdRef.current = projectId;
    } catch {
      setError('Failed to load schedule settings');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [projectId]);

  const loadHolidays = useCallback(async () => {
    if (!projectId) return;
    const country = settings.holiday_country || 'NZ';
    const t0 = performance.now();

    try {
      const { data: pubData } = await supabase
        .from('jphph_schedule_holidays')
        .select('holiday_date, name, holiday_name')
        .eq('country_code', country)
        .eq('is_active', true);
      setHolidays(
        (pubData || []).map((h) => {
          const title =
            (h as Record<string, unknown>).holiday_name ||
            (h as Record<string, unknown>).name ||
            'Holiday';
          return { date: h.holiday_date as string, name: String(title) };
        })
      );
    } catch {
      setHolidays([]);
    }
    try {
      const { data: projData } = await supabase
        .from('jphph_project_schedule_holidays')
        .select('holiday_date, name, holiday_name')
        .eq('project_id', projectId)
        .eq('is_active', true);
      setProjectHolidays(
        (projData || []).map((h) => {
          const title =
            (h as Record<string, unknown>).holiday_name ||
            (h as Record<string, unknown>).name ||
            'Holiday';
          return { date: h.holiday_date as string, name: String(title) };
        })
      );
    } catch {
      setProjectHolidays([]);
    }
    perfLog('loadHolidays', performance.now() - t0);
  }, [projectId, settings.holiday_country]);

  const reload = useCallback(async () => {
    if (projectId) settingsCache.delete(projectId);
    setLoading(true);
    await loadSettings(true);
    setLoading(false);
  }, [loadSettings, projectId]);

  const reloadHolidays = useCallback(async () => {
    await loadHolidays();
  }, [loadHolidays]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return {
    settings,
    holidays,
    projectHolidays,
    loading,
    error,
    reload,
    reloadHolidays,
  };
}
